import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import GlassBackground from "@/shared/components/glassBackground";
import { confirmDialog } from "@/shared/components/dialog";
import { useWorkspaces } from "../hooks/useWorkspaces";
import type { WorkspaceSummary, WorkspaceUpsert } from "../models/Workspace";
import { WorkspacesGrid } from "../components/workspaces_grid";
import { WorkspacesSidebar } from "../components/workspaces_sidebar";
import WorkspaceFormModal from "../components/workspace_form_modal";

export default function WorkspacesManagementPage() {
  const navigate = useNavigate();
  const { loading, error, getAll, create, update, remove } = useWorkspaces();

  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WorkspaceSummary | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    void (async () => {
      const result = await getAll();
      if (!ignore && result) setWorkspaces(result);
    })();
    return () => {
      ignore = true;
    };
  }, [getAll]);

  const stats = {
    total: workspaces.length,
    totalAutomations: workspaces.reduce((sum, w) => sum + w.workflowCount, 0),
  };

  const openForm = (workspace: WorkspaceSummary | null) => {
    setEditing(workspace);
    setFormOpen(true);
  };

  const handleSave = async (upsert: WorkspaceUpsert) => {
    setSaving(true);
    try {
      const saved = editing ? await update(editing.id, upsert) : await create(upsert);
      if (!saved) return;
      setWorkspaces((prev) =>
        editing ? prev.map((w) => (w.id === saved.id ? saved : w)) : [...prev, saved],
      );
      setFormOpen(false);
      setEditing(null);
    } catch {
      // useAsync ya notificó el error vía toast
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (workspace: WorkspaceSummary) => {
    const confirmed = await confirmDialog({
      title: "Eliminar workspace",
      message: `Se eliminará "${workspace.name}" y su dashboard. Las ${workspace.workflowCount} automatizaciones que agrupa NO se eliminan: quedan sin agrupar.`,
      confirmLabel: "Eliminar",
      cancelLabel: "Cancelar",
      variant: "danger",
    });
    if (!confirmed) return;

    setDeletingId(workspace.id);
    try {
      await remove(workspace.id);
      setWorkspaces((prev) => prev.filter((w) => w.id !== workspace.id));
    } catch {
      // useAsync ya notificó el error vía toast
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <GlassBackground>
      <div className="relative flex w-full overflow-hidden">
        <WorkspacesSidebar
          total={stats.total}
          totalAutomations={stats.totalAutomations}
          loading={loading}
          onCreate={() => openForm(null)}
        />

        <main className="flex-1 overflow-auto p-8">
          <WorkspacesGrid
            workspaces={workspaces}
            loading={loading && workspaces.length === 0}
            error={error}
            deletingId={deletingId}
            onOpen={(id) => navigate(`/admin/workspaces/${id}`)}
            onDashboard={(id) => navigate(`/admin/workspaces/${id}/dashboard`)}
            onEdit={openForm}
            onDelete={handleDelete}
            onCreate={() => openForm(null)}
          />
        </main>
      </div>

      {formOpen && (
        <WorkspaceFormModal
          workspace={editing}
          saving={saving}
          onSave={handleSave}
          onClose={() => {
            setFormOpen(false);
            setEditing(null);
          }}
        />
      )}
    </GlassBackground>
  );
}
